/**
 * The elevation notice of the console (plan 2026-10-22-cp-active-role, K8, design direction e,
 * change 1): one slot at the top of the content area that speaks twice per elevation, and is empty
 * at every other moment.
 *
 * Who renders it: `SectionLayout`, under the header and above the rail and the page, on every page.
 * Who feeds it: `useActiveRole()`. Outside a provider it renders nothing. For a person who is never
 * elevated (a person who holds `platform_admin` alone rests as `platform_admin`, K2), it stays empty.
 *
 * The two moments:
 *   1. The warning: from {@link WARNING_LEAD_MS} before the elevation end. An elevation shorter than
 *      the lead (a short elevation length, K7) is in the warning at once. The text does not
 *      tick: it names "10 min" only when about 10 minutes are left as it appears, and the end time
 *      always. "Renew with passkey" asks for the passkey (the `role-elevation` step-up) and restarts
 *      the elevation; "Dismiss" hides the warning for this elevation only.
 *   2. The drop: once, when the elevation ended with no switch from this tab (`ended` of the
 *      context). "Act as <role>" asks for the passkey again; "Dismiss" hides it.
 * A renew that succeeds shows one success line for {@link RENEWED_NOTICE_MS}. A renew or a switch
 * that does not complete keeps its notice and adds one line that says why.
 *
 * Accessibility: the slot is one always-mounted `div` with `role="status"` and `aria-live="polite"`,
 * so a screen reader announces each new notice once. It never moves the focus to a notice. After a
 * passkey dialog closes, `StepUpDialog` returns the focus to the button that opened it; when that
 * button left with its notice, the focus moves to `main#main`, the page content.
 *
 * Side effects: one timer per elevation for the warning, one focus listener while elevated, one
 * timer for the success line, and the requests of the renew and the switch.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, Button } from '@witness-ai/ward-design-system';
import { WardScope } from '../ward/WardScope';
import { ARIA_DISABLED_CLASS } from '../ward/class-tokens';
import {
  actingAsText,
  clockOf,
  elevationName,
  useActiveRole,
  type ActiveRoleValue,
  type RoleSwitchResult,
} from './active-role-context';

/** How long before the elevation end the warning appears: 10 minutes (K8). */
export const WARNING_LEAD_MS = 10 * 60_000;

/** How long the success line of a renew stays: 6 seconds (direction e, change 1). */
export const RENEWED_NOTICE_MS = 6_000;

/** The longest single `setTimeout` delay a browser honours (2^31 - 1 ms). */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * The warning sentence, fixed when the warning appears. With `leftMs` of 9 minutes or more it names
 * "10 min" (the timer fires at 10 minutes, a little late); with less it names the end time alone.
 * With a `restRole` it names the role the session returns to (R-004). Pure.
 */
export function warningText(name: string, untilMs: number, leftMs: number, restRole: string | null): string {
  const end = leftMs >= WARNING_LEAD_MS - 60_000 ? `${name} ends in 10 min, at ${clockOf(untilMs)}.` : `${name} ends at ${clockOf(untilMs)}.`;
  return restRole === null ? `${end} Renew?` : `${end} Then you act as ${actingAsText(restRole)}. Renew?`;
}

/** The line a notice adds when its passkey action did not complete: the cause, then `tail`. */
export function retryLine(result: Exclude<RoleSwitchResult, { readonly kind: 'switched' }>, tail: string): string {
  switch (result.kind) {
    case 'cancelled':
      return `You cancelled the passkey. ${tail}`;
    case 'not-verified':
      return `The passkey did not verify. ${tail}`;
    case 'failed':
      return `The request did not complete. ${result.message} ${tail}`;
  }
}

/**
 * The one notice slot. With an active-role provider it is always mounted and empty at rest. With no
 * provider (a token that carries no `heldSiteRoles` claim) no elevation exists, so it renders nothing.
 */
export function ElevationNotice(): React.JSX.Element | null {
  const active = useActiveRole();
  if (active === null) return null;
  return (
    // `relative`: an `sr-only` child needs a positioned ancestor (the note in `SectionLayout`).
    <div className="relative">
      <div role="status" aria-live="polite" data-testid="elevation-notice-slot" className="px-4 pt-3 empty:sr-only">
        <NoticeBody value={active} />
      </div>
    </div>
  );
}

/** The warning of one elevation: its end, the role name it was written for, and its sentence. */
interface Warning {
  readonly untilMs: number;
  readonly name: string;
  /** The rest role the text names, so a change of it writes the text again. */
  readonly restRole: string | null;
  readonly text: string;
}

/** The extra line of one notice, keyed by the notice it belongs to. */
interface Line {
  readonly key: string;
  readonly text: string;
}

function NoticeBody({ value }: { readonly value: ActiveRoleValue }): React.JSX.Element | null {
  const { elevatedUntilMs: untilMs, activeRole, restRole, ended, now } = value;
  const name = elevationName(activeRole);
  const [warning, setWarning] = useState<Warning | null>(null);
  const [dismissedUntil, setDismissedUntil] = useState<number | null>(null);
  const [dismissedEnded, setDismissedEnded] = useState<number | null>(null);
  const [line, setLine] = useState<Line | null>(null);
  const [renewed, setRenewed] = useState(false);
  const [busy, setBusy] = useState(false);
  /* Set when a passkey action succeeded; the next render moves the focus if its button left. */
  const focusAfter = useRef(false);

  // The warning of this elevation: due now, at its lead time, or when the window gains focus.
  useEffect(() => {
    if (untilMs === null) {
      setWarning(null);
      setDismissedUntil(null);
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = (): void => {
      const leftMs = untilMs - now();
      if (leftMs > WARNING_LEAD_MS) {
        // Early (a capped delay, or clock drift): arm again for the lead time.
        clearTimeout(timer);
        timer = setTimeout(check, Math.min(MAX_TIMER_MS, Math.max(0, leftMs - WARNING_LEAD_MS)));
        return;
      }
      setWarning((current) =>
        current?.untilMs === untilMs && current.name === name && current.restRole === restRole
          ? current
          : { untilMs, name, restRole, text: warningText(name, untilMs, leftMs, restRole) },
      );
    };
    check();
    window.addEventListener('focus', check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', check);
    };
  }, [untilMs, now, name, restRole]);

  // The success line of a renew leaves by itself.
  useEffect(() => {
    if (!renewed) return undefined;
    const timer = setTimeout(() => setRenewed(false), RENEWED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [renewed]);

  useEffect(() => {
    if (!focusAfter.current) return;
    focusAfter.current = false;
    const focused = document.activeElement;
    if (focused === null || focused === document.body || !focused.isConnected) document.getElementById('main')?.focus();
  });

  /*
   * Run one passkey action of a notice: the renew, or the switch back to the ended role. One at a
   * time. A success leaves the notice and moves the focus if it left; any other result adds the line.
   */
  const run = (key: string, send: () => Promise<RoleSwitchResult>, tail: string, onSwitched: () => void): void => {
    if (busy) return;
    setBusy(true);
    setLine(null);
    void send().then((result) => {
      setBusy(false);
      if (result.kind === 'switched') {
        focusAfter.current = true;
        onSwitched();
      } else {
        setLine({ key, text: retryLine(result, tail) });
      }
    });
  };

  if (renewed && untilMs !== null) {
    return (
      <WardScope>
        <Alert variant="success" size="sm" role="group" aria-label="Admin notice" data-testid="elevation-notice-renewed">
          <AlertDescription>
            {name} renewed until {clockOf(untilMs)}.
          </AlertDescription>
        </Alert>
      </WardScope>
    );
  }

  if (untilMs !== null && warning !== null && warning.untilMs === untilMs && dismissedUntil !== untilMs) {
    const key = `warning-${String(untilMs)}`;
    const renew = (): void => run(key, value.renewElevation, `${name} still ends at ${clockOf(untilMs)}.`, () => setRenewed(true));
    return (
      <NoticeCard
        variant="warning"
        testId="elevation-notice-warning"
        text={warning.text}
        line={line?.key === key ? line.text : null}
        actionLabel="Renew with passkey"
        actionTestId="elevation-notice-renew"
        busy={busy}
        onAction={renew}
        onDismiss={() => {
          focusAfter.current = true;
          setDismissedUntil(untilMs);
        }}
      />
    );
  }

  if (untilMs === null && ended !== null && dismissedEnded !== ended.atMs) {
    const key = `ended-${String(ended.atMs)}`;
    const actAgain = (): void =>
      run(key, () => value.switchRole(ended.role), `You still act as ${actingAsText(activeRole)}.`, () => undefined);
    return (
      <NoticeCard
        variant="info"
        testId="elevation-notice-ended"
        text={`${elevationName(ended.role)} ended at ${clockOf(ended.atMs)}. You act as ${actingAsText(activeRole)}.`}
        line={line?.key === key ? line.text : null}
        actionLabel={`Act as ${actingAsText(ended.role)}`}
        actionTestId="elevation-notice-act-again"
        busy={busy}
        onAction={actAgain}
        onDismiss={() => {
          focusAfter.current = true;
          setDismissedEnded(ended.atMs);
        }}
      />
    );
  }

  return null;
}

/** One notice with its passkey action and Dismiss, on the WARD `Alert`. */
function NoticeCard({
  variant,
  testId,
  text,
  line,
  actionLabel,
  actionTestId,
  busy,
  onAction,
  onDismiss,
}: {
  readonly variant: 'warning' | 'info';
  readonly testId: string;
  readonly text: string;
  readonly line: string | null;
  readonly actionLabel: string;
  readonly actionTestId: string;
  readonly busy: boolean;
  readonly onAction: () => void;
  readonly onDismiss: () => void;
}): React.JSX.Element {
  return (
    <WardScope>
      {/* `role="group"` replaces the WARD `role="alert"`: the slot is the polite live region (direction rule 2). */}
      <Alert variant={variant} size="sm" role="group" aria-label="Admin notice" data-testid={testId}>
        <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span data-testid={`${testId}-text`}>{text}</span>
          {line !== null && (
            <span data-testid={`${testId}-line`} className="basis-full">
              {line}
            </span>
          )}
          <span className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              aria-disabled={busy || undefined}
              onClick={onAction}
              data-testid={actionTestId}
              className={ARIA_DISABLED_CLASS}
            >
              {busy ? 'Waiting for the passkey…' : actionLabel}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onDismiss} data-testid={`${testId}-dismiss`}>
              Dismiss
            </Button>
          </span>
        </AlertDescription>
      </Alert>
    </WardScope>
  );
}
